import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, Upload, Loader2, Sparkles, Check } from "lucide-react";
import { API, formatApiError } from "@/contexts/AuthContext";
import { NOTCH } from "@/constants/testIds";

export default function PhotoLog() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    try { const { data } = await axios.get(`${API}/photolog`); setItems(data); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("note", note);
      await axios.post(`${API}/photolog`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNote(""); e.target.value = "";
      await load();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-8 pt-8 pb-24">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C06F52] mb-2">Photo log</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C4C3B] leading-tight">Snap a bowl, save the macros.</h1>
        <p className="text-[#666] mt-2 max-w-lg">Upload a meal photo. Notch's vision model identifies the foods and estimates macros — you can edit before saving.</p>
      </div>

      <div className="rounded-[1.75rem] bg-white border border-[#E5E2DC] p-6 sm:p-8 mb-10">
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#2C4C3B] text-white text-sm font-medium hover:bg-[#1E3629] transition-colors cursor-pointer">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            <span>{busy ? "Analysing your meal…" : "Upload a photo"}</span>
            <input
              data-testid={NOTCH.photoInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onFile}
              disabled={busy}
              className="hidden"
            />
          </label>
          <input
            value={note} onChange={(e)=>setNote(e.target.value)}
            placeholder="Optional note (e.g. 'Lunch — leftover')"
            className="flex-1 min-w-[200px] rounded-full border border-[#D1CDD2] bg-white px-5 py-3 text-sm text-[#222] placeholder:text-[#aaa] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B]"
          />
        </div>
        {err && <div className="mt-4 rounded-2xl bg-[#FBEDE7] border border-[#E9C2B3] text-[#7a3320] px-4 py-2.5 text-sm">{err}</div>}
      </div>

      {items === null && <p className="text-sm text-[#888]">Loading…</p>}
      {items?.length === 0 && (
        <div className="rounded-[1.5rem] bg-[#F2EFE9] border border-[#E5E2DC] p-10 text-center">
          <Camera size={20} className="text-[#C06F52] mx-auto mb-3" />
          <h2 className="font-serif-display text-2xl text-[#2C4C3B] mb-2">No photos yet.</h2>
          <p className="text-sm text-[#666]">Drop a meal photo above to get started.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {items?.map((it, i) => (
          <LogItem key={it.id} item={it} index={i} onSaved={load} />
        ))}
      </div>
    </main>
  );
}

function LogItem({ item, index, onSaved }) {
  const a = item.analysis || {};
  const m = a.estimated_macros || {};
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState({
    calories: m.calories || 0, protein_g: m.protein_g || 0,
    carbs_g: m.carbs_g || 0, fat_g: m.fat_g || 0, fiber_g: m.fiber_g || 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const merged = { ...a, estimated_macros: { ...m, ...vals, calories: Number(vals.calories), protein_g: Number(vals.protein_g), carbs_g: Number(vals.carbs_g), fat_g: Number(vals.fat_g), fiber_g: Number(vals.fiber_g) } };
      await axios.patch(`${API}/photolog/${item.id}`, { analysis: merged });
      setEditing(false); onSaved?.();
    } catch {} finally { setSaving(false); }
  };

  return (
    <article data-testid={NOTCH.photoLogItem(index)} className="bg-white rounded-[1.5rem] border border-[#E5E2DC] overflow-hidden">
      {item.photo_data_url && (
        <img src={item.photo_data_url} alt={a.title || "meal"} className="w-full h-56 object-cover" />
      )}
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#888]">
            {new Date(item.created_at).toLocaleString()}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-[#C06F52]">
            <Sparkles size={11} />conf {(a.confidence ?? 0).toFixed(2)}
          </span>
        </div>
        <h3 className="font-serif-display text-xl text-[#2C4C3B] leading-tight mb-2">{a.title || "Meal"}</h3>
        {a.notes && <p className="text-xs text-[#666] italic mb-4">{a.notes}</p>}
        {a.detected_foods && a.detected_foods.length > 0 && (
          <ul className="flex flex-wrap gap-1.5 mb-4">
            {a.detected_foods.map((f, idx) => (
              <li key={idx} className="text-[11px] bg-[#F2EFE9] text-[#444] px-2.5 py-1 rounded-full">
                {f.name}{f.approx_grams ? ` · ${f.approx_grams}g` : ""}
              </li>
            ))}
          </ul>
        )}

        {!editing ? (
          <div className="flex flex-wrap gap-4 pt-3 border-t border-[#E5E2DC]">
            <Macro label="kcal" value={m.calories} />
            <Macro label="protein" value={`${m.protein_g}g`} />
            <Macro label="carbs" value={`${m.carbs_g}g`} />
            <Macro label="fat" value={`${m.fat_g}g`} />
            {m.fiber_g != null && <Macro label="fibre" value={`${m.fiber_g}g`} />}
            <button onClick={()=>setEditing(true)} className="ml-auto text-xs text-[#2C4C3B] hover:underline self-end">Edit macros</button>
          </div>
        ) : (
          <div className="pt-3 border-t border-[#E5E2DC] grid grid-cols-2 sm:grid-cols-5 gap-2">
            <EditNum testid={NOTCH.photoEditCalories} label="kcal" value={vals.calories} onChange={(v)=>setVals({...vals,calories:v})} />
            <EditNum testid={NOTCH.photoEditProtein} label="protein" value={vals.protein_g} onChange={(v)=>setVals({...vals,protein_g:v})} />
            <EditNum testid={NOTCH.photoEditCarbs} label="carbs" value={vals.carbs_g} onChange={(v)=>setVals({...vals,carbs_g:v})} />
            <EditNum testid={NOTCH.photoEditFat} label="fat" value={vals.fat_g} onChange={(v)=>setVals({...vals,fat_g:v})} />
            <EditNum label="fibre" value={vals.fiber_g} onChange={(v)=>setVals({...vals,fiber_g:v})} />
            <button data-testid={NOTCH.photoSaveEdits} onClick={save} disabled={saving}
              className="col-span-2 sm:col-span-5 inline-flex items-center justify-center gap-1.5 mt-2 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-xs font-medium hover:bg-[#1E3629] disabled:opacity-60">
              <Check size={12} />{saving ? "Saving…" : "Save edits"}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

function Macro({ label, value }) {
  return (
    <div>
      <div className="text-base font-medium text-[#2C4C3B] leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#888] mt-1">{label}</div>
    </div>
  );
}
function EditNum({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.14em] text-[#888] mb-1">{label}</span>
      <input
        data-testid={testid}
        type="number" value={value} onChange={(e)=>onChange(e.target.value)}
        className="w-full rounded-full border border-[#D1CDD2] bg-white px-3 py-1.5 text-sm text-[#222] focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/20 focus:border-[#2C4C3B]"
      />
    </label>
  );
}
